import React, { useState, useEffect } from "react"
import { Card, Checkbox, Typography, Flex, Spin, Empty, Button, Dropdown, Tag } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { PlusOutlined, MoreOutlined } from "@ant-design/icons"
import { getGuideTrip, updateTrip } from "store/trips"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { setFormTouched } from "store/proNewListingForms"
import { useCustomFees } from "pagesV2/pro/Settings/TaxesAndFees/hooks/useCustomFees"
import AddNewFeeModal from "pagesV2/pro/Settings/TaxesAndFees/modal/AddNewFee"
import DeleteConfirmation from "pagesV2/pro/Settings/ProSettings/CustomTemplate/modal/DeleteConfirmation"
import AssociatedExperiencesConfirmModal from "pagesV2/pro/Settings/TaxesAndFees/modal/AssociatedExperiencesConfirmModal"
import { numberToCurrency } from "helpers/string"
import styles from "./index.module.css"

const { Title, Text } = Typography

const FeeItem = ({ fee, checked, onChange, menuItems, isMutating }) => {
  const { t } = useTranslation()
  return (
    <div className={styles.feeItem}>
      <Flex justify="space-between" align="center">
        <Checkbox checked={checked} onChange={(e) => onChange(fee.id, e.target.checked)} disabled={!fee.is_active}>
          <Flex vertical gap={2}>
            <Flex align="center" gap={8}>
              <Text className={styles.feeName}>{fee.title}</Text>
              {!fee.is_active && <Tag color="default">{t("settings.status_inactive")}</Tag>}
            </Flex>
            <Text className={styles.feePrice}>
              {fee.fee_type === "percentage" ? `${Number(fee.price).toFixed(2)}%` : numberToCurrency(fee.price)}
            </Text>
          </Flex>
        </Checkbox>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight" disabled={isMutating}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </Flex>
    </div>
  )
}

FeeItem.propTypes = {
  fee: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fee_type: PropTypes.string.isRequired,
    is_active: PropTypes.bool
  }).isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  menuItems: PropTypes.array.isRequired,
  isMutating: PropTypes.bool.isRequired
}

const ListingTaxFees = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const tripLoading = useSelector(loadingSelector("trips.trip"))

  const {
    customFees,
    customFeesLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    deleteModalOpen,
    feeToEdit,
    handleAddFee,
    handleDeleteConfirm,
    handleDeleteCancel,
    openAddModal,
    closeModal,
    getMenuItems,
    assocExpModalOpen,
    pendingAction,
    associatedExperiences,
    isCheckingAssociations,
    isConfirming,
    handleAssocExpConfirm,
    handleAssocExpCancel,
    activateModalOpen,
    handleActivateConfirm,
    handleActivateCancel,
    feeToActivate
  } = useCustomFees({ enabled: true })

  const [selectedFeeIds, setSelectedFeeIds] = useState([])

  useEffect(() => {
    dispatch(getGuideTrip(id)).then((res) => {
      const ids =
        res?.data?.assigned_custom_fee_ids ||
        res?.data?.assigned_custom_fees?.map((fee) => fee.id)?.filter((feeId) => feeId != null) ||
        []
      setSelectedFeeIds(ids)
    })
  }, [dispatch, id])

  // Keep selectedFeeIds in sync — remove IDs that were deleted or are no longer active
  useEffect(() => {
    if (customFees.length === 0) return
    const activeFeeIds = new Set(customFees.filter((fee) => fee.is_active).map((fee) => fee.id))
    setSelectedFeeIds((prev) => prev.filter((feeId) => activeFeeIds.has(feeId)))
  }, [customFees])

  const handleChange = (feeId, checked) => {
    setSelectedFeeIds((prev) => (checked ? [...prev, feeId] : prev.filter((fId) => fId !== feeId)))
    dispatch(setFormTouched("tax_fees", true))
  }

  const onFinish = (callback) => {
    const formData = new FormData()
    if (selectedFeeIds.length === 0) {
      formData.append("trip[assigned_custom_fee_ids][]", "")
    } else {
      selectedFeeIds.forEach((feeId) => formData.append("trip[assigned_custom_fee_ids][]", feeId))
    }
    formData.append("trip[wizard_step]", "tax_fees")
    dispatch(updateTrip(id, formData))
    if (callback) callback()
  }

  useEffect(() => {
    const handleSaveRequest = async (event) => {
      const { resolve, reject } = event.detail
      try {
        const formData = new FormData()
        if (selectedFeeIds.length === 0) {
          formData.append("trip[assigned_custom_fee_ids][]", "")
        } else {
          selectedFeeIds.forEach((feeId) => formData.append("trip[assigned_custom_fee_ids][]", feeId))
        }
        formData.append("trip[wizard_step]", "tax_fees")
        await dispatch(updateTrip(id, formData))
        dispatch(setFormTouched("tax_fees", false))
        resolve()
      } catch (error) {
        reject(error)
      }
    }
    window.addEventListener("taxFeesSaveRequest", handleSaveRequest)
    return () => window.removeEventListener("taxFeesSaveRequest", handleSaveRequest)
  }, [dispatch, id, selectedFeeIds])

  return (
    <>
      <Spin spinning={customFeesLoading || tripLoading}>
        <Card className={styles.card}>
          <Flex justify="space-between" align="center" className={styles.header} wrap>
            <Title level={3} className={styles.title}>
              {t("listings.tabs.tax_fees.card_title")}
            </Title>
            <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={openAddModal}>
              {t("settings.add_new")}
            </Button>
          </Flex>
          <Text className={styles.description}>{t("listings.tabs.tax_fees.description")}</Text>

          {!customFeesLoading && customFees.length === 0 ? (
            <Empty description={t("listings.tabs.tax_fees.no_fees")} className={styles.empty} />
          ) : (
            <div className={styles.feeList}>
              {customFees.map((fee) => (
                <FeeItem
                  key={fee.id}
                  fee={fee}
                  checked={selectedFeeIds.includes(fee.id)}
                  onChange={handleChange}
                  menuItems={getMenuItems(fee).filter((item) => !(item.key === "toggle" && fee.is_active))}
                  isMutating={isMutating}
                />
              ))}
            </div>
          )}
        </Card>
        <ListingActionButtons onFinish={onFinish} />
      </Spin>

      <AddNewFeeModal
        visible={isModalOpen}
        onCancel={closeModal}
        onAdd={handleAddFee}
        initialValues={feeToEdit}
        isSaving={isCreating || isUpdating}
      />
      <DeleteConfirmation
        visible={deleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("settings.delete_custom_fee_title")}
        loading={isDeleting}
        feeTitle={pendingAction?.feeTitle || ""}
      />
      <DeleteConfirmation
        visible={activateModalOpen}
        onCancel={handleActivateCancel}
        onConfirm={handleActivateConfirm}
        title={t("settings.activate_custom_fee_title")}
        confirmText={t("settings.action_activate")}
        loading={isUpdating}
        feeTitle={feeToActivate?.feeTitle || feeToActivate?.title || ""}
      />
      <AssociatedExperiencesConfirmModal
        visible={assocExpModalOpen}
        onCancel={handleAssocExpCancel}
        onConfirm={handleAssocExpConfirm}
        actionType={pendingAction?.type}
        experiences={associatedExperiences}
        isCheckingAssociations={isCheckingAssociations}
        isConfirming={isConfirming}
        customFeeTitle={pendingAction?.feeTitle || ""}
      />
    </>
  )
}

export default ListingTaxFees
